import {
  Component,
  Input,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ElementRef,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as echarts from 'echarts';

import { GlobalFilterService } from '../../shared-services/global-filter-service';

type ChartRow = {
  month?: string;
  months?: string;
  quantity_predictor?: string | number;
  quantity_actual?: string | number;
  sales_predictor?: string | number;
  sales_actual?: string | number;
  gross_revenue?: string | number;
  net_revenue?: string | number;
  [key: string]: any;
};

type ChartPayload = {
  categories: string[];
  qtyCommitted: number[];
  qtyActual: number[];
  salesCommitted: number[];
  salesActual: number[];
};

@Component({
  selector: 'app-mbar-mdotline',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './mbar-mdotline.component.html',
  styleUrl: './mbar-mdotline.component.scss'
})
export class MbarMdotlineComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() data!: any;

  @ViewChild('chartEl', { static: true }) chartEl!: ElementRef<HTMLDivElement>;

  private chart?: echarts.ECharts;
  private destroy$ = new Subject<void>();
  private pendingRows: ChartRow[] = [];
  private lastRequestSignature = '';

  constructor(
    private globalFilterService: GlobalFilterService,
    private http: HttpClient
  ) { }



  ngOnInit(): void {
    this.fetchFromPlatformApi();





    this.globalFilterService.filter$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.fetchFromPlatformApi();
      });
  }

  ngAfterViewInit(): void {
    this.initChart();

    if (this.pendingRows.length) {
      this.render(this.mapApiRowsToPayload(this.pendingRows));
    }

    window.addEventListener('resize', this.onResize);
    setTimeout(() => this.chart?.resize(), 0);
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.onResize);
    this.destroy$.next();
    this.destroy$.complete();
    this.chart?.dispose();
  }

  private onResize = () => this.chart?.resize();

  private initChart(): void {
    const el = this.chartEl?.nativeElement;
    if (!el) return;

    if (this.chart) this.chart.dispose();
    this.chart = echarts.init(el);
  }

  private mapApiRowsToPayload(rows: ChartRow[]): ChartPayload {
    const cleanMonth = (value: any) => String(value ?? '').replace(/^\uFEFF/, '').trim();
    const toNum = (value: any) => {
      const parsed = Number(String(value ?? '').trim());
      return Number.isFinite(parsed) ? parsed : 0;
    };
    const pick = (...values: any[]) =>
      values.find((value) => value !== undefined && value !== null && value !== '');

    return {
      categories: rows.map((row) => cleanMonth(pick(row.months, row.month))),
      qtyCommitted: rows.map((row) => toNum(pick(row.quantity_predictor, row.gross_revenue, row.sales_predictor))),
      qtyActual: rows.map((row) => toNum(pick(row.quantity_actual, row.net_revenue, row.sales_actual))),
      salesCommitted: rows.map((row) => toNum(pick(row.sales_predictor, row.gross_revenue, row.quantity_predictor))),
      salesActual: rows.map((row) => toNum(pick(row.sales_actual, row.net_revenue, row.quantity_actual)))
    };
  }

  private isInitialLoad = true;

  private fetchFromPlatformApi(): void {
    const apiUrl = this.resolveApiUrl();
    const isInitialRequest = this.isInitialLoad;
    let payload = this.buildPlatformPayload();

    // Use is_active-only requestData for initial load
    if (isInitialRequest) {
      payload = this.buildInitialPayload(payload);
      this.isInitialLoad = false;
    } else if (this.isRequestDataEmpty(payload?.requestData)) {
      return;
    }

    const requestSignature = JSON.stringify(payload);
    if (requestSignature === this.lastRequestSignature) {
      return;
    }
    this.lastRequestSignature = requestSignature;

    this.http.post<any>(apiUrl, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const rows = this.extractRowsFromResponse(response);
          if (!rows.length) {
            console.warn('[mbar-mdotline] No rows found in responseList.');
            return;
          }

          this.pendingRows = rows;
          if (this.chart) {
            this.render(this.mapApiRowsToPayload(rows));
          }
        },
        error: (error: any) => {
          this.lastRequestSignature = '';
          console.error('[mbar-mdotline] API call failed:', error);
        }
      });
  }

  private resolveApiUrl(): string {
    const configuredApiUrl = this.data?.apiUrl || this.data?.platformApiUrl;
    if (typeof configuredApiUrl === 'string' && configuredApiUrl.trim().length > 0) {
      return configuredApiUrl;
    }
    return '${API_BASE_URL}/spriced/platform';
  }

  private buildPlatformPayload(): any {
    const selectedFilters = this.globalFilterService.getCurrentFilters();

    const selectedCustomer = this.firstNonEmptyValue(
      selectedFilters?.requestParams?.customer,
      selectedFilters?.requestParams?.customer_name,
      selectedFilters?.queryParams?.customer,
      selectedFilters?.queryParams?.customer_name
    );

    const selectedLocation = this.firstNonEmptyValue(
      selectedFilters?.requestParams?.location,
      selectedFilters?.requestParams?.region,
      selectedFilters?.queryParams?.location,
      selectedFilters?.queryParams?.region
    );

    const payloadOverride = this.data?.payload || {};
    const requestDataOverride = payloadOverride?.requestData || {};

    const requestData: any = {};
    if (selectedCustomer !== null) {
      requestData.customer = selectedCustomer;
    }
    if (selectedLocation !== null) {
      requestData.location = selectedLocation;
    }
    Object.assign(requestData, requestDataOverride);

    const endpoints = Array.isArray(payloadOverride?.endpoints) && payloadOverride.endpoints.length
      ? payloadOverride.endpoints
      : [{
        functionName: 'CustomerRevenueReadService',
        version: '1.0'
      }];

    return {
      requestData,
      responseList: Array.isArray(payloadOverride?.responseList) ? payloadOverride.responseList : [],
      entity: payloadOverride?.entity || 'customer_revenue',
      batchId: payloadOverride?.batchId || 'test-batch-001',
      workflowId: payloadOverride?.workflowId || 'DISC-123',
      endpoints,
      communicationType: payloadOverride?.communicationType || 'rest',
      version: payloadOverride?.version || '1.0'
    };
  }

  private buildInitialPayload(basePayload: any): any {
    return {
      ...basePayload,
      requestData: {
        is_active: 'true'
      }
    };
  }

  private isRequestDataEmpty(requestData: any): boolean {
    if (!requestData || typeof requestData !== 'object' || Array.isArray(requestData)) {
      return true;
    }

    return Object.keys(requestData).length === 0;
  }

  private firstNonEmptyValue(...values: any[]): string | null {
    for (const value of values) {
      if (Array.isArray(value)) {
        for (const nestedValue of value.flat(Infinity)) {
          if (nestedValue !== null && nestedValue !== undefined && String(nestedValue).trim() !== '') {
            return String(nestedValue);
          }
        }
        continue;
      }

      if (value !== null && value !== undefined && String(value).trim() !== '') {
        return String(value);
      }
    }

    return null;
  }

  private extractRowsFromResponse(response: any): ChartRow[] {
    if (!response) return [];
    if (Array.isArray(response)) return response as ChartRow[];

    const responseList = response?.responseList;
    if (Array.isArray(responseList)) {
      for (const entry of responseList) {
        if (Array.isArray(entry?.data)) {
          return entry.data as ChartRow[];
        }
      }
    }

    if (Array.isArray(response?.data)) {
      return response.data as ChartRow[];
    }

    return [];
  }

  private render(payload: ChartPayload): void {
    if (!this.chart) return;
    const lineSmooth = this.data?.lineSmooth ?? true;

    const colors = {
      salesCommitted: '#2ca02c',
      salesActual: '#8a2be2',
      qtyCommitted: '#ff6b6b',
      qtyActual: '#4c8bf5'
    };

    const option: echarts.EChartsOption = {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      grid: { left: 16, right: 24, top: 30, bottom: 90, containLabel: true },

      legend: {
        bottom: 20,
        padding: 14,
        itemWidth: 28,
        itemHeight: 12,
        borderColor: '#ddd',
        borderWidth: 1,
        borderRadius: 8,
        textStyle: {
          color: '#000',
          fontWeight: 'bold'
        },
        data: ['Gross Revenue', 'Net Revenue']
      },

      xAxis: {
        type: 'category',
        data: payload.categories,
        axisTick: { alignWithLabel: true },
        axisLabel: {
          color: '#000',
          fontWeight: 'bold'
        }
      },

      yAxis: [
        {
          type: 'value',
          name: 'Revenue',
          axisLine: { show: true },
          nameTextStyle: {
            color: '#000',
            fontWeight: 'bold'
          },
          axisLabel: {
            color: '#000',
            fontWeight: 'bold'
          }
        }
      ],

      series: [
        {
          name: 'Gross Revenue',
          type: 'bar',
          yAxisIndex: 0,
          barWidth: 18,
          itemStyle: { color: colors.qtyCommitted, borderRadius: [4, 4, 0, 0] },
          data: payload.qtyCommitted
        },
        {
          name: 'Net Revenue',
          type: 'bar',
          yAxisIndex: 0,
          barWidth: 18,
          barGap: '35%',
          itemStyle: { color: colors.qtyActual, borderRadius: [4, 4, 0, 0] },
          data: payload.qtyActual
        }
      ]
    };

    this.chart.setOption(option, true);
    setTimeout(() => this.chart?.resize(), 0);
  }
}
